import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

const COLORS = ["#0a2342", "#f0a924", "#2d6a4f", "#4a90e2", "#d90429", "#718096"];

export default function BrigadePage() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dashboard/brigade").then(({ data }) => setData(data));
    }, []);

    if (!data)
        return (
            <div className="text-sf-muted" data-testid="brigade-loading">
                Caricamento…
            </div>
        );

    return (
        <div data-testid="brigade-page">
            <div className="mb-10">
                <div className="label-eyebrow mb-2">Analytics</div>
                <h1 className="font-display text-4xl text-navy">
                    Produttività di brigata
                </h1>
                <p className="text-sf-secondary mt-1">
                    Performance per ruolo, copertura turni, ranking persone.
                </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 mb-6">
                <div
                    className="bg-white border border-sf rounded-xl p-6"
                    data-testid="brigade-chart-coverage"
                >
                    <div className="label-eyebrow mb-4">
                        Copertura turni per giorno
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <BarChart data={data.coverage}>
                                <CartesianGrid
                                    stroke="#e2e8f0"
                                    strokeDasharray="3 3"
                                />
                                <XAxis dataKey="day" />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="shifts" fill="#0a2342" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div
                    className="bg-white border border-sf rounded-xl p-6"
                    data-testid="brigade-chart-revenue-by-role"
                >
                    <div className="label-eyebrow mb-4">
                        Incasso per ruolo
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie
                                    data={data.by_role}
                                    dataKey="revenue"
                                    nameKey="role"
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    label
                                >
                                    {data.by_role.map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={COLORS[i % COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Legend />
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div
                className="bg-white border border-sf rounded-xl overflow-hidden"
                data-testid="brigade-table"
            >
                <div className="px-6 py-4 border-b border-sf flex items-center justify-between">
                    <div className="font-display text-2xl text-navy">
                        Ranking brigata
                    </div>
                    <div className="text-xs text-sf-muted">
                        Ordinati per incasso generato
                    </div>
                </div>
                <table className="w-full text-sm">
                    <thead className="bg-[#fafaf7]">
                        <tr className="text-left">
                            <th className="px-4 py-3 label-eyebrow">#</th>
                            <th className="px-4 py-3 label-eyebrow">Nome</th>
                            <th className="px-4 py-3 label-eyebrow">Ruolo</th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Ore
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Coperti
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Incasso
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Mance
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Rating
                            </th>
                            <th className="px-4 py-3 label-eyebrow text-right">
                                Punt. %
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.by_employee.length === 0 && (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="text-center py-10 text-sf-muted"
                                >
                                    Aggiungi dipendenti e performance per
                                    vedere il ranking.
                                </td>
                            </tr>
                        )}
                        {data.by_employee.map((e, i) => (
                            <tr
                                key={e.id}
                                className="border-t border-sf hover:bg-[#fafaf7]"
                            >
                                <td className="px-4 py-3 font-mono text-xs text-sf-muted">
                                    {i + 1}
                                </td>
                                <td className="px-4 py-3 font-medium">
                                    {e.name}
                                </td>
                                <td className="px-4 py-3 text-sf-secondary">
                                    {e.role}
                                </td>
                                <td className="px-4 py-3 text-right font-mono">
                                    {e.hours}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {e.coperti}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-navy">
                                    €{e.revenue.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    €{e.tips.toFixed(2)}
                                </td>
                                <td className="px-4 py-3 text-right text-amber-brand font-semibold">
                                    {e.rating}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    {e.punctuality}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
